import clsx from 'clsx';
import React, {
  ButtonHTMLAttributes,
  forwardRef,
  useMemo,
  useRef,
} from 'react';
import Icon from '../Icon';
import { PlacesType, Tooltip } from 'react-tooltip';
import { uniqueId } from 'lodash-es';
import MeasuredWallpaperZoomButtons, {
  MeasuredWallpaperZoomButtonsProps,
} from './MeasuredWallpaperZoomButtons';

interface OptionButton extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string | React.ReactElement;
  tooltipPosition?: PlacesType;
  tooltip?: string;
}

interface MeasuredWallpaperOptionButtonsProps
  extends MeasuredWallpaperZoomButtonsProps {
  buttons: (OptionButton | undefined)[];
  mainButtonIcon?: string | React.ReactElement;
  rotateOnOpen?: boolean;
  tooltipPosition?: PlacesType;
  showNotificationDot?: boolean;
  showZoomButtons: boolean;
}

const OptionButton = forwardRef<HTMLButtonElement, OptionButton>(
  (
    {
      icon,
      tooltip,
      tooltipPosition,
      className,
      children,
      ...props
    }: OptionButton,
    ref
  ) => {
    const tooltipId = useMemo(() => uniqueId(), []);
    return (
      <>
        <button
          className={clsx(
            'grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-solid border-gray-100 bg-white text-gray-900 disabled:text-gray-300',
            className
          )}
          {...props}
          ref={ref}
          data-tooltip-content={tooltip}
          data-tooltip-id={tooltipId}
        >
          {typeof icon === 'string' ? (
            <Icon name={icon} className="h-5 w-5" />
          ) : (
            icon
          )}
          {children}
        </button>
        <Tooltip
          id={tooltipId}
          place={tooltipPosition}
          delayShow={200}
          delayHide={0}
        />
      </>
    );
  }
);

OptionButton.displayName = 'ExpandingButtonElement';

const MeasuredWallpaperOptionButtons = ({
  mainButtonIcon,
  rotateOnOpen = true,
  tooltipPosition = 'bottom',
  showNotificationDot = false,
  showZoomButtons,
  minZoomRatio,
  maxZoomRatio,
  currentZoomRatio,
  zoomRatioStep,
  visibleZoomLevel,
  onZoom,
  ...props
}: MeasuredWallpaperOptionButtonsProps) => {
  const filteredButtons = useMemo(
    () => props.buttons.filter((btn) => !!btn),
    [props.buttons]
  );

  return (
    <div
      className={clsx(
        'flex items-center gap-2 overflow-x-auto px-4 py-3',
        showZoomButtons
          ? 'justify-start sm:justify-center'
          : 'justify-center'
      )}
    >
      {showZoomButtons && (
        <MeasuredWallpaperZoomButtons
          minZoomRatio={minZoomRatio}
          maxZoomRatio={maxZoomRatio}
          zoomRatioStep={zoomRatioStep}
          currentZoomRatio={currentZoomRatio}
          visibleZoomLevel={visibleZoomLevel}
          onZoom={onZoom}
        />
      )}
      {filteredButtons.map((btn, idx) => (
        <OptionButton
          {...btn!}
          key={idx}
          tooltipPosition={tooltipPosition}
        />
      ))}
    </div>
  );
};

export default MeasuredWallpaperOptionButtons;
